import React, { forwardRef } from 'react';
import { ThemedButtonOld as ThemedButton } from '@jira/platform__project-theme-components/src/ui/themed-button-old/ThemedButtonOld.tsx';
import ShowMoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import type { TriggerProps } from '@atlaskit/popup/types';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import type { CustomTriggerComponentType } from '../types.tsx';
import { EntryPointContent } from './entrypoint-content/index.tsx';

const moreActions = defineMessage({
	id: 'navigation-project-action-menu.more-actions',
	defaultMessage: 'More actions',
	description:
		'Used as the alt text for an icon button that when used shows a popup menu of more options.',
});

// @ts-expect-error: to unblock React 18.2.0 -> 18.3.1 version bump
const CustomTrigger: CustomTriggerComponentType = forwardRef<HTMLButtonElement, TriggerProps>(
	({ ...props }, ref) => {
		const { formatMessage } = useIntl();

		const triggerButton = (
			<ThemedButton
				{...props}
				ref={ref}
				appearance="subtle"
				iconBefore={
					<ShowMoreIcon
						label={formatMessage(moreActions)}
						color="currentColor"
						spacing="spacious"
					/>
				}
				// eslint-disable-next-line jira/integration/test-id-by-folder-structure
				testId="navigation-project-action-menu.ui.themed-button"
			/>
		);

		return (
			<Tooltip content={formatMessage(moreActions)} hideTooltipOnClick>
				{triggerButton}
			</Tooltip>
		);
	},
);

type Props = {
	projectKey: string;
	projectId?: string;
	isCorePremiumUserSeat?: boolean;
	isProjectMenuReorderingEnabled?: boolean;
	additionalMenuItems?: React.ReactNode;
	withThemedTrigger?: boolean;
	onClick?: () => void;
	starred?: boolean;
	recent?: boolean;
	shouldRenderToParent?: boolean;
	actionMenuId?: string;
	projectName?: string;
	/**
	 * When true, this instance will handle opening the edit custom template modal
	 * from the `?editCustomTemplateForProject=<projectId>` query param.
	 *
	 * IMPORTANT: ProjectActionMenu is rendered in TWO locations (sidebar nav and horizontal nav).
	 * Only pass true from the sidebar nav — see ProjectActions.tsx.
	 */
	shouldHandleQueryParamModal?: boolean;
};

export const ProjectActionMenu = ({
	additionalMenuItems,
	isProjectMenuReorderingEnabled = false,
	isCorePremiumUserSeat = false,
	withThemedTrigger = false,
	projectKey,
	projectId,
	onClick,
	shouldRenderToParent,
	actionMenuId,
	projectName,
	shouldHandleQueryParamModal = false,
}: Props): React.JSX.Element | null => {
	const isAnonymous = useIsAnonymous();

	// Anonymous users have no project actions available, so hide the meatball trigger and popup
	// entirely when the experiment is enabled rather than surfacing an unauthorised menu.
	if (isAnonymous && expVal('remove_space_menu_access_for_anonymous_users', 'isEnabled', false)) {
		return null;
	}

	return (
		<EntryPointContent
			projectName={projectName}
			isProjectMenuReorderingEnabled={isProjectMenuReorderingEnabled}
			additionalMenuItems={additionalMenuItems}
			onClick={onClick}
			isCorePremiumUserSeat={isCorePremiumUserSeat}
			CustomTrigger={withThemedTrigger ? CustomTrigger : undefined}
			projectKey={projectKey}
			projectId={projectId}
			shouldRenderToParent={shouldRenderToParent}
			actionMenuId={actionMenuId}
			source={withThemedTrigger ? 'horizontal-nav' : 'sidebar-nav'}
			shouldHandleQueryParamModal={shouldHandleQueryParamModal}
		/>
	);
};
